import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ProgressLoaderComponent } from '../common/progress-loader/progress-loader.component'
import { TranslatePipe } from '@ngx-translate/core'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { DocumentUploadInfo, truncateStringFromMiddle } from '../utils'
import { MAX_AGENDA_SIZE_IN_BYTES } from '../call-document-upload/call-document-upload.component'
import { AgendaUploadStatus, TemplateSourceType } from '../calls.service'

@Component({
  selector: 'app-template-source-picker',
  standalone: true,
  imports: [MatIconModule, MatTooltipModule, ProgressLoaderComponent, TranslatePipe, FileUploadComponent],
  templateUrl: './template-source-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './template-source-picker.component.scss',
})
export class TemplateSourcePickerComponent {
  @Input()
  canEdit: boolean = true
  @Input()
  maxAgendaSize: number = MAX_AGENDA_SIZE_IN_BYTES
  @Input()
  status: AgendaUploadStatus = 'idle'
  @Input()
  failureMessageKey: string | null = null
  @Input()
  uploadedFileName: string | null = null
  @Input()
  templateSourceType: TemplateSourceType | null = null
  @Input()
  requiresConfirmation: boolean = false

  @Output()
  agendaFileChange = new EventEmitter<DocumentUploadInfo>()
  @Output()
  confirmationRequested = new EventEmitter<void>()
  @Output()
  createNewClicked = new EventEmitter<void>()
  @Output()
  loadSavedClicked = new EventEmitter<void>()

  @ViewChild('fileUpload')
  fileUploadComponent: FileUploadComponent | undefined

  isDragging = false

  get isUploadInProgress(): boolean {
    return this.status === 'uploading' || (this.fileUploadComponent?.isUploading ?? false)
  }

  get canStartUpload(): boolean {
    return this.canEdit && !this.isUploadInProgress && this.status !== 'checking'
  }

  get displayFileName(): string | null {
    return this.uploadedFileName !== null ? truncateStringFromMiddle(this.uploadedFileName, 30) : null
  }

  onUploadCardClick() {
    if (this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.openFileDialog()
    }
  }

  openFileDialog() {
    if (this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.openFileDialog()
    }
  }

  onFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo !== null) {
      this.agendaFileChange.emit(documentUploadInfo)
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = true
  }

  onDragLeave() {
    this.isDragging = false
  }

  onDrop(event: DragEvent) {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false
    const files = event.dataTransfer?.files ?? null
    if (files !== null && files.length > 0 && this.fileUploadComponent !== undefined) {
      this.fileUploadComponent.fileInputChanged({ target: { files } })
    }
  }
}
